import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { cardsAPI, vcardAPI, leadsAPI } from '@/lib/api';
import {
  User, Download, Phone, Mail, Globe, Linkedin,
  Instagram, Twitter, MapPin, Building2, Share2
} from 'lucide-react';

const iconMap = {
  LinkedIn: Linkedin,
  Instagram: Instagram,
  Twitter: Twitter,
  Website: Globe,
};

export default function NFCCardPage() {
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const res = await cardsAPI.getPublic(id);
        setCard(res.data);
        // Track lead (anonymous view)
        try {
          await leadsAPI.create({
            card_id: res.data.id,
            guest_name: 'Anonymous Visitor',
            guest_info: { source: 'nfc_tap', timestamp: new Date().toISOString() },
          });
        } catch (e) { /* silent */ }
      } catch (err) {
        toast.error('Card not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [id]);

  const handleSaveContact = () => {
    if (!card) return;
    // Trigger real VCF download
    const downloadUrl = vcardAPI.download(card.slug);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${card.name.replace(/ /g, '_')}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSaved(true);
    toast.success('Contact file downloading!');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: card?.name, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="pt-24 pb-20 flex items-center justify-center min-h-screen">
        <div className="w-full max-w-sm mx-auto px-4 space-y-4">
          <Skeleton className="h-[500px] rounded-xl" />
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="pt-24 pb-20 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-title">Card Not Found</h2>
          <p className="text-body mt-2">This NFC card does not exist or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-20 flex items-center justify-center min-h-screen">
      <div className="w-full max-w-sm mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="bg-card border-border overflow-hidden">
            {/* Header with Company Logo */}
            <div className="relative h-28 overflow-hidden">
              <div className="absolute inset-0 bg-secondary" />
              <div className="absolute inset-0 grid-pattern opacity-50" />
              
              {/* Company Logo - Top Right */}
              {card.company_logo && (
                <div className="absolute top-4 right-4 z-10">
                  <div className="w-12 h-12 rounded-lg bg-card/90 backdrop-blur-sm border border-border p-1.5 flex items-center justify-center">
                    <img 
                      src={card.company_logo} 
                      alt={card.company_name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
              
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-10">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full neon-ring p-[3px]">
                    <div className="w-full h-full rounded-full bg-card flex items-center justify-center overflow-hidden">
                      {card.photo_url ? (
                        <img src={card.photo_url} alt={card.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-10 h-10 text-primary" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile */}
            <div className="pt-16 pb-6 px-6 text-center">
              <h1 className="text-xl font-bold text-title">{card.name}</h1>
              <p className="text-sm text-primary mt-1">{card.title}</p>
              <div className="flex items-center justify-center gap-1.5 mt-2">
                <Building2 className="w-3 h-3 text-body" />
                <p className="text-xs text-body">{card.company_name}</p>
              </div>
              {card.bio && (
                <p className="text-xs text-body mt-4 leading-relaxed">{card.bio}</p>
              )}

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleSaveContact}
                  className="flex-1 gap-2 bg-primary text-primary-foreground hover:bg-primary/90 text-sm shadow-silver sheen-effect"
                  disabled={saved}
                >
                  <Download className="w-4 h-4" />
                  {saved ? 'Saved!' : 'Save Contact'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="gap-2 border-border text-title hover:bg-secondary"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Separator className="bg-border" />

            {/* Contact details */}
            <div className="px-6 py-4 space-y-3">
              {[
                { icon: Phone, text: card.phone },
                { icon: Mail, text: card.email },
                { icon: Globe, text: card.website },
                { icon: MapPin, text: card.location },
              ].filter(item => item.text).map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-secondary flex items-center justify-center">
                    <Icon className="w-3.5 h-3.5 text-body" />
                  </div>
                  <span className="text-sm text-title">{text}</span>
                </div>
              ))}
            </div>

            {card.social_links && card.social_links.length > 0 && (
              <>
                <Separator className="bg-border" />
                <div className="px-6 py-4">
                  <p className="text-xs text-body mb-3 uppercase tracking-wider">Connect</p>
                  <div className="grid grid-cols-2 gap-2">
                    {card.social_links.map(({ platform, url }) => {
                      const Icon = iconMap[platform] || Globe;
                      return (
                        <a
                          key={platform}
                          href={url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-secondary border border-border text-sm text-title hover:border-primary/20 hover:bg-primary/5"
                          style={{ transition: 'border-color 0.2s ease, background 0.2s ease' }}
                        >
                          <Icon className="w-4 h-4 text-body" />
                          {platform}
                        </a>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div className="px-6 py-4 bg-secondary/50 flex items-center justify-center gap-2">
              <Badge variant="outline" className="text-[10px] border-primary/20 text-primary bg-primary/5">
                NFC Powered by The Ace
              </Badge>
            </div>
          </Card>

          <Separator className="bg-border my-8" />

          {/* Contact Form */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-title mb-2 text-center">
              Get in Touch
            </h3>
            <p className="text-sm text-body text-center mb-6">
              Interested in connecting? Share your details and let&apos;s start a conversation.
            </p>
            <LeadCaptureForm 
              cardId={card.id}
              buttonText="שלח הודעה / Send Message"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
